import React, { memo, useCallback, useState, useEffect } from "react";
import { IconUserPlus } from "@tabler/icons-react";
import ChatHeader from "./ChatHeader";
import ChatMessages from "./ChatMessages";
import ChatInput from "./ChatInput";
import { useMessages, useServiceActions } from "../../hooks";
import { useServiceCenter } from "../../contexts";
import { SERVICE_STATUS } from "../../utils/constants";
import "./ChatWindow.css";

/**
 * Janela principal do chat
 */
const ChatWindow = memo(({
  onBack,
  onViewContactInfo,
  onOpenTransferModal,
  onOpenFinishModal,
}) => {
  const {
    selectedConversation,
    selectedContact,
    selectedAccount,
  } = useServiceCenter();

  // Derivar botStatus de selectedConversation (1 = ativo, 0 = desativado)
  const botStatus = selectedConversation?.bot_status ?? 1;

  const {
    messages,
    isLoading: isLoadingMessages,
    isSending,
    hasMoreMessages,
    loadMoreMessages,
    sendMessage,
    sendAudioMessage,
    sendFileMessage,
  } = useMessages();

  // Estado de reply de comentário Instagram. Quando setado, próximo envio
  // vai como POST /{comment_id}/replies (sem janela 24h). Ao cancelar ou
  // mudar de conversa, limpa.
  const [replyContext, setReplyContext] = useState(null);

  useEffect(() => {
    // Limpa o contexto ao trocar de conversa.
    setReplyContext(null);
  }, [selectedConversation?.id]);

  const handleReplyComment = useCallback((message) => {
    if (!message?.comment_id) return;
    setReplyContext({
      commentId: message.comment_id,
      mediaPostId: message.media_post_id,
      snippet: (message.body || message.text || '').slice(0, 80),
      senderName: message.sender_name || '',
    });
  }, []);

  const cancelReply = useCallback(() => setReplyContext(null), []);

  const {
    startService,
    updateBotStatus,
    clearAgentMemory,
  } = useServiceActions();

  // Toggle do bot (1 = ativo, 0 = desativado)
  const handleToggleBot = useCallback(async () => {
    const newStatus = botStatus === 1 ? 0 : 1;
    // Normalizar telefone (remover +55 e caracteres especiais)
    const phone = selectedConversation?.phone?.replace(/\D/g, "").replace(/^55/, "");
    await updateBotStatus(phone, newStatus, selectedAccount?.unique);
  }, [botStatus, selectedConversation?.phone, selectedAccount?.unique, updateBotStatus]);

  // Iniciar atendimento
  const handleStartService = useCallback(async () => {
    if (selectedConversation?.contact_id) {
      await startService(selectedConversation.contact_id);
    } else {
      console.warn(
        "[ServiceCenter][StartService] Bloqueado: selectedConversation sem contact_id",
        { selectedConversation }
      );
    }
  }, [selectedConversation?.contact_id, startService]);

  // Limpar memória do agente
  const handleResetMemory = useCallback(async () => {
    if (selectedConversation) {
      await clearAgentMemory(); // Usa selectedConversation do context
    }
  }, [selectedConversation, clearAgentMemory]);

  // Enviar mensagem de texto. Se houver replyContext (resposta a comentário
  // IG), passa adiante e limpa o estado após o envio.
  const handleSendMessage = useCallback(async (text) => {
    if (!selectedConversation) return;
    const ok = await sendMessage(text, { replyContext });
    if (ok && replyContext) {
      setReplyContext(null);
    }
  }, [selectedConversation, sendMessage, replyContext]);

  // Enviar áudio
  const handleSendAudio = useCallback(async (audioBlob, audioUrl, duration) => {
    if (selectedConversation) {
      await sendAudioMessage(audioBlob, audioUrl, duration);
    }
  }, [selectedConversation, sendAudioMessage]);

  // Enviar arquivo (imagem, vídeo, documento)
  const handleSendFile = useCallback(async (file, caption) => {
    if (selectedConversation) {
      await sendFileMessage(file, caption);
    }
  }, [selectedConversation, sendFileMessage]);

  // Verificar se o chat está em atendimento ativo
  const isInService = selectedConversation?.service_status === SERVICE_STATUS.IN_SERVICE;

  // Se não há conversa selecionada
  if (!selectedConversation) {
    return (
      <div className="sc-chat-window sc-chat-window--empty">
        <div className="sc-chat-window__empty-content">
          <span className="sc-chat-window__empty-icon">💬</span>
          <h3 className="sc-chat-window__empty-title">
            Central de Atendimento
          </h3>
          <p className="sc-chat-window__empty-text">
            Selecione uma conversa para começar a atender
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="sc-chat-window">
      <ChatHeader
        conversation={selectedConversation}
        contact={selectedContact}
        botStatus={botStatus}
        onBack={onBack}
        onToggleBot={handleToggleBot}
        onStartService={handleStartService}
        onTransfer={onOpenTransferModal}
        onFinish={onOpenFinishModal}
        onViewInfo={onViewContactInfo}
        onResetMemory={handleResetMemory}
      />

      <ChatMessages
        messages={messages}
        isLoading={isLoadingMessages}
        hasMore={hasMoreMessages}
        onLoadMore={loadMoreMessages}
        onReplyComment={handleReplyComment}
      />

      {/* Overlay para iniciar atendimento */}
      {!isInService && (
        <div className="sc-chat-window__start-overlay">
          <button
            className="sc-chat-window__start-btn"
            onClick={handleStartService}
          >
            <IconUserPlus size={20} />
            <span>Iniciar Atendimento</span>
          </button>
        </div>
      )}

      <ChatInput
        onSendMessage={handleSendMessage}
        onSendAudio={handleSendAudio}
        onSendFile={handleSendFile}
        disabled={!isInService}
        isSending={isSending}
        placeholder={
          isInService
            ? "Digite uma mensagem..."
            : "Inicie o atendimento para enviar mensagens"
        }
        replyContext={replyContext}
        onCancelReply={cancelReply}
      />
    </div>
  );
});

ChatWindow.displayName = "ChatWindow";

export default ChatWindow;
